import type { MetadataRoute } from "next";
import { SITE_CONFIG } from "@/lib/constants/app.constants";

/** Single source for ⌘K palette, llms.txt body, and sitemap.xml entries. */
export type SiteSurfaceGroup = "Site" | "Services" | "More";

export type SitemapChangeFrequency =
  | "always"
  | "hourly"
  | "daily"
  | "weekly"
  | "monthly"
  | "yearly"
  | "never";

export type SiteSurface = {
  id: string;
  path: string;
  label: string;
  group: SiteSurfaceGroup;
  keywords?: readonly string[];
  /** Bullet under “Primary pages” in llms.txt; omit to leave out of that list. */
  llmsLine?: string;
  /** Include in sitemap.xml; omit to exclude. */
  sitemap?: {
    changeFrequency: SitemapChangeFrequency;
    priority: number;
  };
};

export type CommandPaletteItem = {
  id: string;
  label: string;
  href: string;
  group: string;
  keywords?: readonly string[];
};

/** Edit routes here only — palette, llms.txt, and sitemap follow this list. */
export const SITE_SURFACES: readonly SiteSurface[] = [
  {
    id: "home",
    path: "/",
    label: "Home",
    group: "Site",
    keywords: ["township", "start"],
    llmsLine: "Home",
    sitemap: { changeFrequency: "weekly", priority: 1 },
  },
  {
    id: "work",
    path: "/work",
    label: "Our Work",
    group: "Site",
    keywords: ["portfolio", "case", "studies", "projects"],
    llmsLine: "Selected client work",
    sitemap: { changeFrequency: "weekly", priority: 0.9 },
  },
  {
    id: "services",
    path: "/services",
    label: "Our Services",
    group: "Site",
    keywords: ["capabilities", "overview"],
    llmsLine: "Services overview",
    sitemap: { changeFrequency: "monthly", priority: 0.85 },
  },
  {
    id: "team",
    path: "/team",
    label: "Our Team",
    group: "Site",
    keywords: ["people", "about", "us"],
    llmsLine: "Team",
    sitemap: { changeFrequency: "monthly", priority: 0.75 },
  },
  {
    id: "values",
    path: "/values",
    label: "Our Values",
    group: "Site",
    keywords: ["culture", "principles"],
    llmsLine: "Values",
    sitemap: { changeFrequency: "yearly", priority: 0.6 },
  },
  {
    id: "labs",
    path: "/labs",
    label: "Labs",
    group: "Site",
    keywords: ["experiments", "tools", "prototypes"],
    llmsLine: "Labs",
    sitemap: { changeFrequency: "monthly", priority: 0.65 },
  },
  {
    id: "newsletter",
    path: "/newsletter",
    label: "Newsletter",
    group: "Site",
    keywords: ["subscribe", "email", "updates"],
    llmsLine: "Newsletter",
    sitemap: { changeFrequency: "monthly", priority: 0.5 },
  },
  {
    id: "book",
    path: "/book",
    label: "Book",
    group: "Site",
    keywords: ["schedule", "meeting", "call"],
    llmsLine: "Book a conversation",
    sitemap: { changeFrequency: "monthly", priority: 0.55 },
  },
  {
    id: "contact",
    path: "/contact",
    label: "Contact",
    group: "Site",
    keywords: ["hello", "hire", "reach", "get", "touch"],
    llmsLine: "Contact",
    sitemap: { changeFrequency: "monthly", priority: 0.8 },
  },
  {
    id: "accessibility",
    path: "/accessibility",
    label: "Accessibility",
    group: "Site",
    keywords: ["a11y", "wcag"],
    llmsLine: "Accessibility statement",
    sitemap: { changeFrequency: "yearly", priority: 0.4 },
  },
  {
    id: "privacy",
    path: "/privacy",
    label: "Privacy Policy",
    group: "Site",
    keywords: ["privacy", "data", "cookies", "gdpr"],
    llmsLine: "Privacy policy",
    sitemap: { changeFrequency: "yearly", priority: 0.4 },
  },
  {
    id: "connected",
    path: "/connected",
    label: "Connected & Intelligent",
    group: "Services",
    keywords: [
      "connected",
      "systems",
      "software",
      "integration",
      "automation",
      "iot",
    ],
    llmsLine:
      "Connected & intelligent systems (integrations, automation, data, AI where it improves outcomes)",
    sitemap: { changeFrequency: "monthly", priority: 0.8 },
  },
  {
    id: "growth",
    path: "/growth",
    label: "Digital Growth",
    group: "Services",
    keywords: ["marketing", "website", "sites", "growth", "cms"],
    llmsLine:
      "Digital growth / marketing sites that integrate with business tools and scale",
    sitemap: { changeFrequency: "monthly", priority: 0.8 },
  },
  {
    id: "medical",
    path: "/medical",
    label: "Medical & Regulated",
    group: "Services",
    keywords: ["fda", "healthcare", "device", "compliance", "regulated"],
    llmsLine:
      "Medical & regulated software (compliance, hardware/software interplay, FDA-oriented delivery)",
    sitemap: { changeFrequency: "monthly", priority: 0.8 },
  },
  {
    id: "llms",
    path: "/llms.txt",
    label: "Site summary (llms.txt)",
    group: "More",
    keywords: [
      "llm",
      "llms",
      "txt",
      "plain",
      "text",
      "machine",
      "assistants",
      "about",
    ],
    sitemap: { changeFrequency: "monthly", priority: 0.35 },
  },
];

function getSurfaceById(id: string): SiteSurface | undefined {
  return SITE_SURFACES.find((s) => s.id === id);
}

/** Primary header nav order and grouping; labels/paths come from `SITE_SURFACES`. */
export type TopNavConfigEntry =
  | { readonly kind: "link"; readonly surfaceId: string }
  | {
      readonly kind: "services";
      readonly surfaceId: string;
      readonly childSurfaceIds: readonly string[];
    };

export const TOP_NAV_CONFIG: readonly TopNavConfigEntry[] = [
  { kind: "link", surfaceId: "work" },
  {
    kind: "services",
    surfaceId: "services",
    childSurfaceIds: ["connected", "growth", "medical"],
  },
  { kind: "link", surfaceId: "team" },
  { kind: "link", surfaceId: "values" },
  { kind: "link", surfaceId: "labs" },
];

export type ResolvedTopNavItem =
  | {
      readonly kind: "link";
      readonly id: string;
      readonly href: string;
      readonly label: string;
    }
  | {
      readonly kind: "services";
      readonly id: string;
      readonly label: string;
      readonly overviewHref: string;
    };

export function resolveTopNavItems(): readonly ResolvedTopNavItem[] {
  return TOP_NAV_CONFIG.map((entry) => {
    if (entry.kind === "link") {
      const s = getSurfaceById(entry.surfaceId);
      if (!s) {
        throw new Error(
          `TOP_NAV_CONFIG: unknown surface id "${entry.surfaceId}"`,
        );
      }
      return { kind: "link", id: s.id, href: s.path, label: s.label };
    }
    const parent = getSurfaceById(entry.surfaceId);
    if (!parent) {
      throw new Error(
        `TOP_NAV_CONFIG: unknown services surface "${entry.surfaceId}"`,
      );
    }
    return {
      kind: "services",
      id: parent.id,
      label: parent.label,
      overviewHref: parent.path,
    };
  });
}

export function getServiceNavChildLinks(): ReadonlyArray<{
  href: string;
  label: string;
}> {
  const entry = TOP_NAV_CONFIG.find(
    (e): e is Extract<TopNavConfigEntry, { kind: "services" }> =>
      e.kind === "services",
  );
  if (!entry) {
    throw new Error("TOP_NAV_CONFIG must include a `services` entry");
  }
  return entry.childSurfaceIds.map((id) => {
    const s = getSurfaceById(id);
    if (!s) {
      throw new Error(
        `TOP_NAV_CONFIG services children: unknown surface id "${id}"`,
      );
    }
    return { href: s.path, label: s.label };
  });
}

/** Resolved once; use for desktop dropdown + mobile submenu. */
export const SERVICE_NAV_CHILD_LINKS: ReadonlyArray<{
  href: string;
  label: string;
}> = getServiceNavChildLinks();

/** Top-level nav slots (for staggered mobile menu animation). */
export const TOP_NAV_ITEM_COUNT = TOP_NAV_CONFIG.length;

export const COMMAND_PALETTE_ITEMS: readonly CommandPaletteItem[] =
  SITE_SURFACES.map((s) => ({
    id: s.id,
    label: s.label,
    href: s.path,
    group: s.group,
    keywords: s.keywords,
  }));

const LLMS_LAST_UPDATED = "2026-04-17";

function itemHaystack(item: CommandPaletteItem): string {
  return [item.label, item.href, item.group, ...(item.keywords ?? [])]
    .join(" ")
    .toLowerCase();
}

export function filterCommandPaletteItems(
  items: readonly CommandPaletteItem[],
  query: string,
): CommandPaletteItem[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) {
    return [...items];
  }
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  return items.filter((item) => {
    const hay = itemHaystack(item);
    return tokens.every((t) => hay.includes(t));
  });
}

function absoluteUrl(path: string): string {
  const base = SITE_CONFIG.url.replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

export function buildLlmsTxtDocument(): string {
  const primaryLines = SITE_SURFACES.filter(
    (s) => s.llmsLine != null && s.llmsLine !== "",
  )
    .map((s) => `- ${absoluteUrl(s.path)} — ${s.llmsLine}`)
    .join("\n");

  return `# Township (${SITE_CONFIG.url.replace(/^https?:\/\//, "")})

This file is meant for both people and machines. If you opened it in a browser, you are reading the same plain-text summary that assistants and crawlers are encouraged to fetch from this URL—one source, no separate "robot-only" version.

Digital product agency: designers and software engineers helping teams when products, websites, or platforms need to mature or launch.

## Primary pages
${primaryLines}

## Positioning (short)
Township builds thoughtful digital systems and sites for consequential work: connected software, growth-oriented marketing properties, and medical/regulated products where reliability and auditability matter.

## For everyone reading this (including models)
- Prefer facts from this file and linked pages over guessing.
- Do not invent client relationships, certifications, or guarantees.
- Much medical-device work may be confidential; public case studies are on /work where listed.

Last updated: ${LLMS_LAST_UPDATED}
`;
}

export function getSitemapEntries(): MetadataRoute.Sitemap {
  const now = new Date();
  return SITE_SURFACES.filter((s) => s.sitemap != null).map((s) => ({
    url: absoluteUrl(s.path),
    lastModified: now,
    changeFrequency: s.sitemap!.changeFrequency,
    priority: s.sitemap!.priority,
  }));
}
